import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { fetchCarInfoDetails } from './carinfodetailsAPI';

const initialState = { 
  data: null, 
  vehiclesArray: [], // Pre-calculated for instant rendering
  status: 'idle', 
  error: null 
};

export const fetchCarInfoDetailsWithFilters = createAsyncThunk(
  'carinfodetails/fetch',
  async (args, { signal, rejectWithValue }) => {
    try {
      const res = await fetchCarInfoDetails({ ...args, signal });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const carinfodetailsSlice = createSlice({
  name: 'carinfodetails',
  initialState,
  reducers: { 
    resetCarInfoState: () => initialState 
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCarInfoDetailsWithFilters.pending, (s) => { 
        s.status = 'loading'; 
        s.error = null; 
      })
      .addCase(fetchCarInfoDetailsWithFilters.fulfilled, (s, { payload }) => {
        s.status = 'succeeded';
        s.data = payload;
        
        // PERFORMANCE BOOST: Transform object to array ONCE here
        // This prevents the selector from re-calculating on every render cycle
        const vByVin = payload?.dataByVin;
        s.vehiclesArray = vByVin ? Object.values(vByVin) : [];
      })
      .addCase(fetchCarInfoDetailsWithFilters.rejected, (s, { payload }) => {
        s.status = 'failed';
        s.error = payload;
      });
  },
});

export const { resetCarInfoState } = carinfodetailsSlice.actions;

// --- High-Performance Selectors ---

// 1. Direct access selectors (Zero calculation, O(1) speed)
export const selectCarInfoDetailsinfoStatus = (s) => s.carinfodetails.status;
export const selectVehicles = (s) => s.carinfodetails.vehiclesArray;
export const selectCarInfoDetailsinfoError = (s) => s.carinfodetails.error;

// 2. Memoized complex selectors
const selectRawData = (s) => s.carinfodetails.data;

export const selectVehiclesByVin = createSelector(
  [selectRawData], 
  (d) => d?.dataByVin || {}
);

export const selectCarInfoDetails = createSelector(
  [selectRawData], 
  (d) => d?.car || null
);

export const selectVehicleByVin = createSelector(
  [selectVehiclesByVin, (_, vin) => vin], 
  (v, vin) => v?.[vin] || null
);

export default carinfodetailsSlice.reducer;